import { BitsentryServicesProvider } from "@bitsentry-ce/components/services/context";
import { cacheExecution } from "./query/execution-cache";
import { queryClient } from "./query/client";
import {
  cacheDeletedRunbook,
  saveRunbookMutation,
  dashboardKeys,
  cacheIncidentThread,
  cacheIncidentArchive,
} from "./query/dashboard";
import React, { useEffect, useMemo } from "react";
import {
  type AuditLogExportResponse,
  type BitsentryServicePorts,
  type CreateErrorSourceInput,
  type CurrentUser,
  type DiagnosisResultsResponse,
  type UpdateErrorSourceInput,
  type PaginatedUsers,
  type RunbookContextV1,
  type RunbookExecutionRecord,
  type RunbookExecutionSummaryRecord,
  type RunbookExportArtifactV1,
  type RunbookLlmProviderKey,
  type RunbookTriggerContext,
  type TelemetryActionConfig,
  type SettingsServicePort,
  type SystemSettingsView,
  type PluginDescriptor,
  type LLMProviderListResponse,
} from "@bitsentry-ce/components/services";
import {
  TicketResolutionProvider,
  type DiagnosisTicket,
  type PaginatedResolvedTickets,
  type ResolvedTicketDetails,
  type ResolvedTicketsSummary,
  type SyncResult,
  type TicketResolutionService,
} from "@bitsentry/components/ticket-resolution";
import {
  executionDetailSchema,
  runbookExecutionEventSchema,
  type RunbookExecutionEvent,
} from "@bitsentry-ce/core/features/runbooks";
import type {
  AllSettingsDto,
  AuditLog,
  GeneralSettingsDto,
  LLMProviderDto,
  ListModelsRequest,
  ListModelsResponse,
  NotificationSettingsDto,
  SaveProviderRequest,
  TestConnectionRequest,
  TestConnectionResponse,
  User,
} from "@bitsentry-ce/components/types/api.types";
import type { DataPolicy } from "@bitsentry/components/settings/data-policy-settings.data";
import { dataPolicySchema } from "@bitsentry/core/features/settings/settings.schemas";
import { SANDBOX_TOKEN_BUDGET_METADATA_KEY } from "@bitsentry/core/features/sandbox-token-budget";
import {
  errorSourceRowSchema,
  errorSourceSyncResultSchema,
} from "@bitsentry-ce/core/features/error-sources/error-sources.schemas";
import {
  cloudPluginDescriptorSchema,
  cloudPluginExecutionResultSchema,
  cloudPluginListResponseSchema,
} from "@bitsentry/core/features/plugins";
import {
  llmProviderSchema,
  listProvidersResponseSchema,
  listModelsResponseSchema,
  testConnectionResponseSchema,
} from "@bitsentry/core/features/llm-providers";
import type {
  ResolutionType as CoreResolutionType,
  TicketPriority as CoreTicketPriority,
  TicketProviderType as CoreTicketProviderType,
} from "@bitsentry/core/features/tickets";
import { z } from "zod";

import { useAuth } from "./auth";
import { trpcClient } from "./trpc/client";
import { useWebSocket } from "./websocket";
import { webSocketService } from "./websocket/service";
import { createCloudRunbookGateway } from "./runbook-gateway";
import {
  approveRunbookAuthoringProposal,
  normalizeRunbookAuthoringProposal,
  normalizeRunbookAuthoringDecision,
} from "./runbook-authoring";

interface FrontendBitsentryServicesProviderProps {
  children: React.ReactNode;
}

type IncidentsPort = NonNullable<BitsentryServicePorts["incidents"]>;
type FrontendSettingsServicePort = SettingsServicePort & {
  getDataPolicy(): Promise<DataPolicy>;
};
export type FrontendBitsentryServicePorts = Omit<
  BitsentryServicePorts,
  "settings"
> & {
  settings: FrontendSettingsServicePort;
};
type RawRunbookExecution = Awaited<
  ReturnType<typeof trpcClient.runbooksRouter.getExecution.query>
>;
type RawRunbookExecutionSummaryList = Awaited<
  ReturnType<typeof trpcClient.runbooksRouter.listExecutions.query>
>;
const isoDateSchema = z.union([
  z.string(),
  z.date().transform((value) => value.toISOString()),
]);

const numericIdSchema = z.union([
  z.number().int(),
  z
    .string()
    .regex(/^\d+$/)
    .transform((value) => Number.parseInt(value, 10)),
]);

const relationSchema = z.object({
  id: numericIdSchema,
  name: z.string(),
});

const dashboardRunbookProviderKeySchema = z.enum([
  "groq",
  "kilocode",
  "openai",
  "anthropic",
  "gemini",
  "openrouter",
]);

const runbookProviderKeySchema = z.union([
  dashboardRunbookProviderKeySchema,
  z.enum(["claude_code", "codex"]),
]);

type DashboardRunbookProviderKey = z.infer<
  typeof dashboardRunbookProviderKeySchema
>;

function toDashboardErrorSourcePlugins(value: unknown): PluginDescriptor[] {
  return cloudPluginListResponseSchema.parse(value);
}

function unsupportedWebPluginOperation(operation: string): Promise<never> {
  return Promise.reject(
    new Error(`${operation} is managed by the Dashboard worker plugin host.`),
  );
}

function normalizeRunbookProviderKey(
  provider: string | null | undefined,
): RunbookLlmProviderKey | undefined {
  const result = runbookProviderKeySchema.safeParse(provider);

  if (!result.success) {
    return undefined;
  }

  return result.data;
}

function createWorkosManagedAuthError(action: string): Error {
  return new Error(
    `${action} is managed by WorkOS in this environment. Continue with WorkOS instead.`,
  );
}

function rejectWorkosManagedAuth<T>(action: string): Promise<T> {
  return Promise.reject(createWorkosManagedAuthError(action));
}

function toDashboardRunbookProviderKey(
  provider: RunbookLlmProviderKey | undefined,
): DashboardRunbookProviderKey | undefined {
  const result = dashboardRunbookProviderKeySchema.safeParse(provider);

  if (!result.success) {
    return undefined;
  }

  return result.data;
}

function sanitizeTelemetryConfigForDashboard(
  config: TelemetryActionConfig | undefined,
):
  | (Omit<TelemetryActionConfig, "llmProviderKey"> & {
      llmProviderKey?: DashboardRunbookProviderKey;
    })
  | undefined {
  if (config === undefined) {
    return undefined;
  }

  return {
    ...config,
    llmProviderKey: toDashboardRunbookProviderKey(config.llmProviderKey),
  };
}

function sanitizeRunbookActionForDashboard<
  T extends {
    llmProviderKey?: RunbookLlmProviderKey;
    telemetryConfig?: TelemetryActionConfig;
  },
>(
  action: T,
): Omit<T, "llmProviderKey" | "telemetryConfig"> & {
  llmProviderKey?:
    | "groq"
    | "kilocode"
    | "openai"
    | "anthropic"
    | "gemini"
    | "openrouter";
  telemetryConfig?: ReturnType<typeof sanitizeTelemetryConfigForDashboard>;
} {
  return {
    ...action,
    llmProviderKey: toDashboardRunbookProviderKey(action.llmProviderKey),
    telemetryConfig: sanitizeTelemetryConfigForDashboard(
      action.telemetryConfig,
    ),
  };
}

function normalizeTriggerContext(
  triggerContext: RunbookTriggerContext | undefined,
) {
  if (
    triggerContext === undefined ||
    triggerContext.needId === "" ||
    triggerContext.needLabel === "" ||
    triggerContext.sourceId === ""
  ) {
    return;
  }

  return {
    needId: triggerContext.needId,
    needLabel: triggerContext.needLabel,
    sourceId: triggerContext.sourceId,
    sourceName: triggerContext.sourceName,
    sourceType: triggerContext.sourceType,
    entrypoint: triggerContext.entrypoint,
    incidentThreadId: triggerContext.incidentThreadId,
  };
}

const rawUserSchema = z.object({
  id: numericIdSchema,
  email: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  role: relationSchema.nullable().optional(),
  status: relationSchema.nullable().optional(),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
  deletedAt: z.union([isoDateSchema, z.null()]).optional(),
});

type RawUser = z.infer<typeof rawUserSchema>;
const diagnosisResultsSchema = z.object({
  records: z.array(
    z.object({
      id: z.number(),
      telemetry_entry_id: z.number().optional(),
      source_category: z.string(),
      source_kind: z.string(),
      source_table_name: z.string(),
      source_field_name: z.string(),
      source_key_value: z.string(),
      log_level: z.enum(["infrastructure", "application", "unknown"]),
      severity: z.enum([
        "critical",
        "high",
        "medium",
        "low",
        "info",
        "unknown",
      ]),
      category: z.string(),
      category_confidence: z.number().optional(),
      description: z.string().optional(),
      environment: z.string().optional(),
      source_metadata: z.record(z.string(), z.unknown()).optional(),
      normalized_data: z.record(z.string(), z.unknown()).optional(),
      state_history: z.array(
        z.object({
          fromState: z.string().nullable(),
          toState: z.string(),
          transitionedAt: z.string(),
          metadata: z.record(z.string(), z.unknown()).optional(),
        }),
      ),
      state_texts: z.string(),
      created_at: z.string(),
      updated_at: z.string(),
      rule_description: z.string().optional(),
      agent_name: z.string().optional(),
      rule_level: z.number().optional(),
    }),
  ),
  total_count: z.number(),
});

const diagnosisTicketSchema = z.object({
  id: z.string(),
  diagnosisId: z.number(),
  telemetryEntryId: z.number(),
  externalTicketId: z.string(),
  externalTicketNumber: z.string(),
  ticketTitle: z.string().nullable().optional(),
  ticketProvider: z.string(),
  ticketUrl: z.string().nullable(),
  ticketStatus: z.enum([
    "NEW",
    "OPEN",
    "PENDING",
    "IN_PROGRESS",
    "RESOLVED",
    "CLOSED",
    "CANCELLED",
  ]),
  ticketPriority: z.enum(["LOW", "MEDIUM", "HIGH", "URGENT", "CRITICAL"]),
  ticketCreatedAt: isoDateSchema,
  ticketUpdatedAt: isoDateSchema,
  ticketResolvedAt: z.union([isoDateSchema, z.null()]),
  ruleDescription: z.string().nullable(),
  ruleLevel: z.number().nullable(),
  agentName: z.string().nullable(),
  agentIp: z.string().nullable(),
  stateTexts: z.string().nullable(),
  diagnosisState: z.string().nullable(),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
});

const resolvedTicketDetailsSchema = z.object({
  id: z.string(),
  diagnosisId: z.number(),
  telemetryEntryId: z.number().nullable(),
  externalTicketId: z.string(),
  externalTicketNumber: z.string(),
  ticketProvider: z.string(),
  ticketUrl: z.string().nullable(),
  ticketStatus: z.string(),
  ticketPriority: z.enum(["LOW", "MEDIUM", "HIGH", "URGENT", "CRITICAL"]),
  ticketCreatedAt: isoDateSchema,
  ticketUpdatedAt: z.union([isoDateSchema, z.null()]),
  ticketResolvedAt: z.union([isoDateSchema, z.null()]),
  lastSyncedAt: z.union([isoDateSchema, z.null()]),
  lastSyncError: z.string().nullable(),
  resolutionType: z
    .enum(["FULLY_CLOSED", "WORKAROUND", "PARTIALLY_CLOSED"])
    .nullable(),
  lessonsLearned: z.string().nullable(),
  resolutionNotes: z.string().nullable(),
  providerResolutionCode: z.string().nullable(),
  providerSolution: z.string().nullable(),
  providerResolveSeconds: z.number().nullable(),
  resolutionNoteSyncStatus: z.string().nullable(),
  resolutionNoteSyncError: z.string().nullable(),
  ruleDescription: z.string().nullable(),
  agentName: z.string().nullable(),
});

const paginatedResolvedTicketsSchema = z.object({
  data: z.array(resolvedTicketDetailsSchema),
  meta: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
});

const resolvedTicketsSummarySchema = z.object({
  total: z.number(),
  byResolutionType: z.record(z.string(), z.number()),
  avgResolutionTimeHours: z.number(),
  avgItopResolutionTimeHours: z.number().nullable(),
  withLessonsLearned: z.number(),
});

const syncResultSchema = z.object({
  synced: z.number(),
  failed: z.number(),
  errors: z.array(z.object({ externalId: z.string(), error: z.string() })),
  duration: z.number(),
});

const generalSettingsSchema = z.object({
  organizationName: z.string(),
  timezone: z.string(),
  dateFormat: z.string(),
  timeFormat: z.string(),
  language: z.string(),
  defaultDashboard: z.string(),
  maintenanceMode: z.boolean(),
  maintenanceMessage: z.string().optional(),
  lastUsedExternalSourceId: z.string().nullable().optional(),
});

const securityPolicySchema = z.looseObject({});

const notificationSettingsSchema = z.object({
  emailEnabled: z.boolean(),
  emailServer: z.string().optional(),
  emailPort: z.number().optional(),
  emailSecure: z.boolean().optional(),
  emailFrom: z.string().optional(),
  slackEnabled: z.boolean(),
  slackWebhookUrl: z.string().optional(),
  teamsEnabled: z.boolean(),
  teamsWebhookUrl: z.string().optional(),
  webhookEnabled: z.boolean(),
  webhookUrl: z.string().optional(),
  webhookHeaders: z.record(z.string(), z.unknown()).optional(),
});

const allSettingsSchema = z.object({
  general: generalSettingsSchema,
  security: securityPolicySchema,
  notifications: notificationSettingsSchema,
  alertRules: z.array(z.unknown()),
});

const systemSettingsViewSchema = generalSettingsSchema
  .extend({
    systemTimezone: z.string().optional(),
  })
  .transform(({ systemTimezone, ...settings }) => {
    return {
      ...settings,
      systemTimezone: systemTimezone ?? settings.timezone,
    };
  });

const usersResponseSchema = z.object({
  data: z.array(rawUserSchema),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

const rawAuditLogSchema = z.object({
  id: z.string(),
  action: z.string(),
  resource: z.string(),
  resourceId: z.string().nullable().optional(),
  userId: numericIdSchema.nullable().optional(),
  userEmail: z.string().nullable().optional(),
  user: z
    .object({
      id: numericIdSchema.nullable().optional(),
      email: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  ipAddress: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: isoDateSchema,
});

const auditLogsResponseSchema = z.object({
  data: z.array(rawAuditLogSchema),
  meta: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
});

function toUser(input: RawUser): User {
  return {
    id: input.id,
    email: input.email,
    firstName: input.firstName ?? undefined,
    lastName: input.lastName ?? undefined,
    role: input.role ?? undefined,
    status: input.status ?? undefined,
    createdAt: input.createdAt,
    updatedAt: input.updatedAt,
    deletedAt: input.deletedAt ?? undefined,
  };
}

function parseUser(input: unknown): User {
  return toUser(rawUserSchema.parse(input));
}

function parseCurrentUser(input: unknown): CurrentUser | null {
  if (input == null) {
    return null;
  }

  const parsed = rawUserSchema.parse(input);

  return {
    ...toUser(parsed),
  };
}

function parseUsersResponse(input: unknown): PaginatedUsers {
  const parsed = usersResponseSchema.parse(input);

  return {
    data: parsed.data.map(toUser),
    hasNextPage: parsed.hasNextPage,
    hasPreviousPage: parsed.hasPreviousPage,
  };
}

function getAuditLogUserId(
  input: z.infer<typeof rawAuditLogSchema>,
): number | undefined {
  const relatedUserId = input.user?.id;

  if (relatedUserId !== null && relatedUserId !== undefined) {
    return relatedUserId;
  }

  return input.userId ?? undefined;
}

function getAuditLogUserEmail(
  input: z.infer<typeof rawAuditLogSchema>,
): string | undefined {
  const relatedUserEmail = input.user?.email;

  if (relatedUserEmail !== null && relatedUserEmail !== undefined) {
    return relatedUserEmail;
  }

  return input.userEmail ?? undefined;
}

function parseAuditLog(input: z.infer<typeof rawAuditLogSchema>): AuditLog {
  return {
    id: input.id,
    action: input.action,
    resource: input.resource,
    resourceId: input.resourceId ?? undefined,
    userId: getAuditLogUserId(input),
    userEmail: getAuditLogUserEmail(input),
    ipAddress: input.ipAddress ?? undefined,
    userAgent: input.userAgent ?? undefined,
    metadata: input.metadata ?? undefined,
    createdAt: input.createdAt,
  };
}

function parseAuditLogsResponse(input: unknown) {
  const parsed = auditLogsResponseSchema.parse(input);

  return {
    data: parsed.data.map(parseAuditLog),
    meta: parsed.meta,
  };
}

function parseSystemSettingsView(input: unknown): SystemSettingsView {
  return systemSettingsViewSchema.parse(input);
}

async function createErrorSource(input: CreateErrorSourceInput) {
  if (input.sourceType !== "posthog") {
    return trpcClient.errorSourcesRouter.create.mutate(input);
  }

  const setupValues = input.setupValues ?? {};
  const selectedLocation = setupValues.location;
  const hasBaseUrl =
    typeof setupValues.baseUrl === "string" &&
    setupValues.baseUrl.trim().length > 0;
  const regionBaseUrl =
    selectedLocation === "eu"
      ? "https://eu.posthog.com"
      : selectedLocation === "us"
        ? "https://us.posthog.com"
        : undefined;

  if (selectedLocation === "self_hosted" && !hasBaseUrl) {
    throw new Error("Self-hosted PostHog requires a base URL.");
  }

  if (hasBaseUrl || regionBaseUrl === undefined) {
    return trpcClient.errorSourcesRouter.create.mutate(input);
  }

  return trpcClient.errorSourcesRouter.create.mutate({
    ...input,
    setupValues: {
      ...setupValues,
      baseUrl: regionBaseUrl,
    },
  });
}

function buildAgentLlmInput(
  llm:
    | {
        providerKey?: string;
        model?: string;
        thinkingEnabled?: boolean;
      }
    | undefined,
) {
  if (llm === undefined) {
    return;
  }

  if (llm.providerKey === undefined || llm.providerKey === "") {
    return;
  }

  return {
    providerKey: llm.providerKey,
    model: llm.model,
    thinkingEnabled: llm.thinkingEnabled,
  };
}

function buildAgentSelectionOptions(input: {
  accessLevel?: "auto-accept-edits" | "full-access";
  traitValues?: Record<string, string | boolean>;
}) {
  return {
    accessLevel: input.accessLevel,
    traitValues: input.traitValues,
  };
}

function sanitizeRunbookActionForUpdate<
  T extends {
    llmProviderKey?: RunbookLlmProviderKey;
    telemetryConfig?: TelemetryActionConfig;
  },
>(action: T) {
  const sanitized = sanitizeRunbookActionForDashboard(action);

  if (!("id" in action) || typeof action.id !== "string") {
    return sanitized;
  }

  return {
    ...sanitized,
    id: action.id,
  };
}

function sanitizeRunbookActionForSave<
  T extends {
    llmProviderKey?: RunbookLlmProviderKey;
    telemetryConfig?: TelemetryActionConfig;
  },
>(action: T, sortOrder: number | undefined) {
  const sanitized = sanitizeRunbookActionForDashboard(action);

  if (sortOrder === undefined) {
    return sanitized;
  }

  return {
    ...sanitized,
    sortOrder,
  };
}

type RawRunbookExecutionRecord = NonNullable<RawRunbookExecution>;
type RawRunbookExecutionStep = RawRunbookExecutionRecord["steps"][number];
type RawRunbookExecutionSummary =
  RawRunbookExecutionSummaryList["executions"][number];

function normalizeRunbookExecutionStep(step: RawRunbookExecutionStep) {
  const metadata = step.metadata ?? undefined;
  const visibleStructuredOutput = Object.fromEntries(
    Object.entries(step.structuredOutput ?? {}).filter(
      ([key]) => key !== SANDBOX_TOKEN_BUDGET_METADATA_KEY,
    ),
  );

  return {
    actionId: step.actionId,
    order: step.order,
    type: step.type,
    title: step.title,
    status: step.status,
    input: step.input ?? undefined,
    startedAt: step.startedAt,
    completedAt: step.completedAt,
    output: step.output,
    error: step.error,
    metadata,
    structuredOutput:
      Object.keys(visibleStructuredOutput).length > 0
        ? visibleStructuredOutput
        : undefined,
  };
}

function getRunbookExecutionOptionalFields(
  execution: RawRunbookExecutionRecord,
) {
  return {
    completionReason: execution.completionReason ?? undefined,
    idleTimeoutMinutes: execution.idleTimeoutMinutes ?? undefined,
    lastActivityAt: execution.lastActivityAt ?? undefined,
    parameterValues: execution.parameterValues ?? undefined,
    triggerContext: execution.triggerContext ?? undefined,
  };
}

function getRunbookExecutionSummaryOptionalFields(
  summary: RawRunbookExecutionSummary,
) {
  return {
    completionReason: summary.completionReason ?? undefined,
    idleTimeoutMinutes: summary.idleTimeoutMinutes ?? undefined,
    lastActivityAt: summary.lastActivityAt ?? undefined,
    triggerContext: summary.triggerContext ?? undefined,
    currentActionLabel: summary.currentActionLabel ?? undefined,
  };
}

function getRunbookExecutionSummaryResultFields(
  summary: RawRunbookExecutionSummary,
) {
  return {
    modelUsed: summary.modelUsed ?? undefined,
    failureReason: summary.failureReason ?? undefined,
    telemetryEntryIds: summary.telemetryEntryIds ?? undefined,
    diagnosisEntryIds: summary.diagnosisEntryIds ?? undefined,
  };
}

function normalizeRunbookExecution(
  execution: RawRunbookExecution,
): RunbookExecutionRecord | null {
  if (execution === null) {
    return null;
  }

  const steps = execution.steps.map(normalizeRunbookExecutionStep);

  return {
    executionId: execution.executionId,
    runbookId: execution.runbookId,
    incidentThreadId: execution.incidentThreadId,
    runbookTitle: execution.runbookTitle,
    status: execution.status,
    snapshotVersion: execution.snapshotVersion ?? 0,
    startedAt: execution.startedAt,
    completedAt: execution.completedAt,
    ...getRunbookExecutionOptionalFields(execution),
    source: execution.source,
    steps,
  };
}

function normalizeGatewayRunbookExecution(execution: RawRunbookExecution) {
  if (execution === null) {
    return null;
  }
  const normalized = normalizeRunbookExecution(execution);
  if (normalized === null) {
    return null;
  }

  return executionDetailSchema.parse({
    ...normalized,
    incidentThreadId: normalized.incidentThreadId ?? undefined,
    runbookRevisionNumber: execution.runbookRevisionNumber ?? undefined,
  });
}

function normalizeRunbookExecutionSummary(
  summary: RawRunbookExecutionSummary | null,
): RunbookExecutionSummaryRecord | null {
  if (summary === null) {
    return null;
  }

  return {
    executionId: summary.executionId,
    runbookId: summary.runbookId,
    incidentThreadId: summary.incidentThreadId,
    runbookTitle: summary.runbookTitle,
    status: summary.status,
    snapshotVersion: summary.snapshotVersion ?? 0,
    startedAt: summary.startedAt,
    completedAt: summary.completedAt,
    stepCount: summary.stepCount,
    completedStepCount: summary.completedStepCount,
    ...getRunbookExecutionSummaryOptionalFields(summary),
    ...getRunbookExecutionSummaryResultFields(summary),
    source: summary.source ?? undefined,
    providerUsed: normalizeRunbookProviderKey(summary.providerUsed),
  };
}

function isActiveExecutionStatus(
  status: RunbookExecutionSummaryRecord["status"],
) {
  return status === "queued" || status === "pending" || status === "running";
}

export function FrontendBitsentryServicesProvider({
  children,
}: FrontendBitsentryServicesProviderProps) {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { isConnected } = useWebSocket();
  const scope = String(user?.id ?? "anonymous");

  useEffect(() => {
    if (scope === "anonymous") return;
    return webSocketService.on("runbook:execution", (data) => {
      const event = data as { execution: RawRunbookExecution };
      const execution = normalizeRunbookExecution(event.execution);
      if (execution !== null) cacheExecution(scope, execution);
    });
  }, [scope]);

  // Dashboard-specific tRPC and websocket details stay here. Shared incident
  // UI only reaches execution through the product-neutral gateway contract.
  const cloudRunbookGateway = useMemo(
    () =>
      createCloudRunbookGateway({
        listExecutable: () =>
          trpcClient.runbooksRouter.listExecutableRunbooks.query(),
        getRunbookContext: (runbookId) =>
          trpcClient.runbooksRouter.exportRunbookContext.query({
            id: runbookId,
          }),
        start: (request) =>
          trpcClient.runbooksRouter.startRunbookExecution.mutate(request),
        get: async (executionId) =>
          normalizeGatewayRunbookExecution(
            await trpcClient.runbooksRouter.getExecution.query({ executionId }),
          ),
        getLatestForIncidentThread: async (incidentId) =>
          normalizeGatewayRunbookExecution(
            await trpcClient.runbooksRouter.getLatestExecutionForIncidentThread.query(
              {
                incidentId,
              },
            ),
          ),
        subscribe: (listener) =>
          webSocketService.on("runbook:execution", (data) => {
            const rawEvent = data as {
              resultId: string;
              executionId: string;
              incidentThreadId: string | null;
              execution: RawRunbookExecution;
            };
            const execution = normalizeGatewayRunbookExecution(
              rawEvent.execution,
            );
            if (execution === null) {
              return;
            }
            const event: RunbookExecutionEvent =
              runbookExecutionEventSchema.parse({
                eventId: `snapshot:${rawEvent.executionId}:${String(execution.snapshotVersion ?? 0)}`,
                occurredAt:
                  execution.completedAt ??
                  execution.lastActivityAt ??
                  execution.startedAt,
                resultId: rawEvent.resultId,
                executionId: rawEvent.executionId,
                incidentId: rawEvent.incidentThreadId,
                execution,
              });
            listener(event);
          }),
        cancel: async (executionId) => {
          await trpcClient.runbooksRouter.cancelExecution.mutate({
            executionId,
          });
        },
      }),
    [],
  );

  const staticServices = useMemo<
    Omit<FrontendBitsentryServicePorts, "runtime">
  >(
    () => ({
      diagnosis: {
        getDiagnosisResults: async (params = {}) =>
          diagnosisResultsSchema.parse(
            await trpcClient.diagnosisRouter.getResults.query(params),
          ) as DiagnosisResultsResponse,
      },
      settings: {
        async getSystemSettings() {
          return parseSystemSettingsView(
            await trpcClient.settingsRouter.getGeneralSettings.query(),
          );
        },
        getSecuritySettings: () =>
          rejectWorkosManagedAuth("Security policy management"),
        getDataPolicy: async () =>
          dataPolicySchema.parse(
            await trpcClient.settingsRouter.getDataPolicy.query(),
          ) as DataPolicy,
        getIntegrationSettings: async () =>
          allSettingsSchema.parse(
            await trpcClient.settingsRouter.getAllSettings.query(),
          ) as unknown as AllSettingsDto,
        updateSystemSettings: async (data) =>
          generalSettingsSchema.parse(
            await trpcClient.settingsRouter.updateGeneralSettings.mutate({
              data,
            }),
          ) as GeneralSettingsDto,
        updateSecuritySettings: (_data) =>
          rejectWorkosManagedAuth("Security policy management"),
        updateNotificationSettings: async (data) =>
          notificationSettingsSchema.parse(
            await trpcClient.settingsRouter.updateNotificationSettings.mutate({
              data,
            }),
          ) as NotificationSettingsDto,
      },
      globalVariables: {
        list: async () =>
          await trpcClient.globalVariablesRouter.listGlobals.query(),
        create: async (input) =>
          await trpcClient.globalVariablesRouter.createGlobal.mutate(input),
        update: async (id, patch) =>
          await trpcClient.globalVariablesRouter.updateGlobal.mutate({
            id,
            patch,
          }),
        delete: async (id) =>
          await trpcClient.globalVariablesRouter.deleteGlobal.mutate({ id }),
      },
      users: {
        getUsers: async (params = {}) =>
          parseUsersResponse(
            await trpcClient.usersRouter.findAll.query({
              ...(params as unknown as Record<string, unknown>),
              page: params.page ?? 1,
              limit: params.limit ?? 10,
            } as Parameters<typeof trpcClient.usersRouter.findAll.query>[0]),
          ),
        updateUser: async (input) => {
          return parseUser(
            await trpcClient.usersRouter.update.mutate({
              id: Number(input.id),
              data: input.data,
            } as Parameters<typeof trpcClient.usersRouter.update.mutate>[0]),
          );
        },
      },
      auditLogs: {
        getAuditLogs: async (params = {}) =>
          parseAuditLogsResponse(
            await trpcClient.auditLogsRouter.findAll.query(params),
          ),
        exportAuditLogs: async (params = {}) =>
          (await trpcClient.auditLogsRouter.exportLogs.query(
            params,
          )) as AuditLogExportResponse,
      },
      auth: {
        getCurrentUser: async () => {
          const response = await trpcClient.authRouter.getCurrentUser.query();
          return parseCurrentUser(response);
        },
        sendEmailOtp: (_input) => rejectWorkosManagedAuth("Email OTP sign-in"),
        verifyEmailOtp: (_input) =>
          rejectWorkosManagedAuth("Email OTP sign-in"),
        sendMagicLink: (_input) =>
          rejectWorkosManagedAuth("Magic link sign-in"),
        verifyMagicLink: (_input) =>
          rejectWorkosManagedAuth("Magic link sign-in"),
        verifyTotpFor2FA: (_input) =>
          rejectWorkosManagedAuth("Two-factor sign-in"),
        setupTotp: (_input) => rejectWorkosManagedAuth("TOTP management"),
        enableTotp: (_input) => rejectWorkosManagedAuth("TOTP management"),
        getTotpStatus: () => rejectWorkosManagedAuth("TOTP management"),
        generatePasskeyRegistrationOptions: (_input) =>
          rejectWorkosManagedAuth("Passkey management"),
        verifyPasskeyRegistration: (_input) =>
          rejectWorkosManagedAuth("Passkey management"),
        generatePasskeyAuthenticationOptions: (_input) =>
          rejectWorkosManagedAuth("Passkey sign-in"),
        verifyPasskeyAuthentication: (_input) =>
          rejectWorkosManagedAuth("Passkey sign-in"),
        getPasskeys: () => rejectWorkosManagedAuth("Passkey management"),
        deletePasskey: (_input) =>
          rejectWorkosManagedAuth("Passkey management"),
      },
      llmProviders: {
        listProviders: async () =>
          listProvidersResponseSchema.parse(
            await trpcClient.llmProvidersRouter.listProviders.query(),
          ) as LLMProviderListResponse,
        saveProvider: async (request: SaveProviderRequest) =>
          llmProviderSchema.parse(
            await trpcClient.llmProvidersRouter.saveProvider.mutate(request),
          ) as LLMProviderDto,
        setPrimaryProvider: async (providerId: string) =>
          llmProviderSchema.array().parse(
            await trpcClient.llmProvidersRouter.setPrimaryProvider.mutate({
              providerId,
            }),
          ) as LLMProviderDto[],
        listModels: async (request: ListModelsRequest) =>
          listModelsResponseSchema.parse(
            await trpcClient.llmProvidersRouter.listModels.mutate(request),
          ) as ListModelsResponse,
        testConnection: async (request: TestConnectionRequest) =>
          testConnectionResponseSchema.parse(
            await trpcClient.llmProvidersRouter.testConnection.mutate(request),
          ) as TestConnectionResponse,
        deleteProvider: async (providerId: string) =>
          await trpcClient.llmProvidersRouter.deleteProvider.mutate({
            providerId,
          }),
      },
      plugins: {
        list: async () =>
          toDashboardErrorSourcePlugins(
            await trpcClient.dashboardPluginsRouter.list.query(),
          ),
        get: async (pluginId) =>
          cloudPluginDescriptorSchema.parse(
            await trpcClient.dashboardPluginsRouter.describe.query({
              pluginId,
            }),
          ),
        listAvailable: (indexUrl = "") =>
          Promise.resolve({
            indexUrl,
            data: [],
          }),
        installFromIndex: (name) =>
          unsupportedWebPluginOperation(`Installing plugin ${name}`),
        installFromArtifact: (_artifactBase64) =>
          unsupportedWebPluginOperation("Installing plugins from an artifact"),
        getStoredAuth: (_pluginId) => Promise.resolve({}),
        updateStoredAuth: (_pluginId, auth) => Promise.resolve(auth),
        clearStoredAuth: (_pluginId) => Promise.resolve(),
        execute: async (input) =>
          cloudPluginExecutionResultSchema.parse(
            await trpcClient.dashboardPluginsRouter.execute.mutate({
              ...input,
              auth: input.auth ?? {},
              input: input.input ?? {},
            }),
          ),
      },
      errorSources: {
        getAll: async () =>
          errorSourceRowSchema
            .array()
            .parse(await trpcClient.errorSourcesRouter.getAll.query()),
        create: async (input: CreateErrorSourceInput) => {
          const result = await createErrorSource(input);

          return errorSourceRowSchema.parse(result);
        },
        update: async (input: UpdateErrorSourceInput) => {
          await trpcClient.errorSourcesRouter.update.mutate(input);
        },
        delete: async (id) => {
          await trpcClient.errorSourcesRouter.delete.mutate({ id });
        },
        sync: async (id, options) =>
          errorSourceSyncResultSchema.parse(
            await trpcClient.errorSourcesRouter.sync.mutate({
              id,
              logLevelThreshold: options.logLevelThreshold,
              syncEnabled: options.syncEnabled,
            }),
          ),
      },
      agent: {
        start: async (input) => {
          const result = await trpcClient.agentExecutionRouter.start.mutate({
            prompt: input.prompt,
            timeoutMs: input.timeoutMs,
            attachments: input.attachments?.map((attachment) => ({
              ...attachment,
            })),
            llm: buildAgentLlmInput(input.llm),
            ...buildAgentSelectionOptions(input),
            runbookId: input.runbookId,
            incidentThreadId: input.incidentThreadId,
          });
          return { sessionId: result.sessionId };
        },
        send: async (input) => {
          const result = await trpcClient.agentExecutionRouter.send.mutate({
            message: input.message,
            sessionId: input.sessionId,
            attachments: input.attachments?.map((attachment) => ({
              ...attachment,
            })),
            llm: buildAgentLlmInput(input.llm),
            ...buildAgentSelectionOptions(input),
            runbookId: input.runbookId,
            incidentThreadId: input.incidentThreadId,
          });
          return { sessionId: result.sessionId };
        },
        cancel: async (sessionId) => {
          await trpcClient.agentExecutionRouter.cancel.mutate({ sessionId });
        },
        getStatus: async (sessionId) => {
          const result = await trpcClient.agentExecutionRouter.getStatus.query({
            sessionId,
          });
          return {
            sessionId: result.sessionId,
            state: result.state,
            startedAt: result.startedAt,
            currentToolCallId: result.currentToolCallId,
          };
        },
        getSnapshot: async () => null,
        listRunbookAuthoringProposals: async (input) => {
          const result =
            await trpcClient.runbooksRouter.listAuthoringProposals.query(input);
          return result.map(normalizeRunbookAuthoringProposal);
        },
        approveRunbookAuthoringProposal: async (input) => {
          return approveRunbookAuthoringProposal(
            input,
            (request) =>
              trpcClient.runbooksRouter.approveAuthoringProposal.mutate(
                request,
              ),
            scope,
          );
        },
        rejectRunbookAuthoringProposal: async (input) =>
          normalizeRunbookAuthoringDecision(
            await trpcClient.runbooksRouter.rejectAuthoringProposal.mutate(
              input,
            ),
          ),
        requestRunbookAuthoringRevision: async (input) =>
          normalizeRunbookAuthoringDecision(
            await trpcClient.runbooksRouter.requestAuthoringRevision.mutate(
              input,
            ),
          ),
        restoreRunbookAuthoringProposal: async (input) =>
          normalizeRunbookAuthoringDecision(
            await trpcClient.runbooksRouter.restoreAuthoringProposal.mutate(
              input,
            ),
          ),
        onEvent: (handler) => {
          // Subscribe to agent events via WebSocket
          return webSocketService.on("agent:event", (data) => {
            handler(data as Parameters<typeof handler>[0]);
          });
        },
      },
      incidents: {
        listThreads: (async (options) => {
          const listThreadsQuery = trpcClient.incidentsRouter.listThreads
            .query as IncidentsPort["listThreads"];
          return listThreadsQuery(options);
        }) satisfies IncidentsPort["listThreads"],
        getThreadDetail: async (threadId) =>
          trpcClient.incidentsRouter.getThreadDetail.query({ threadId }),
        createThread: async (input) => {
          const thread =
            await trpcClient.incidentsRouter.createThread.mutate(input);
          await cacheIncidentThread(scope, thread, true);
          return thread;
        },
        updateThread: async (input) => {
          const thread =
            await trpcClient.incidentsRouter.updateThread.mutate(input);
          if (thread !== null) await cacheIncidentThread(scope, thread);
          return thread;
        },
        archiveThread: async (threadId) => {
          const result = await trpcClient.incidentsRouter.archiveThread.mutate({
            threadId,
          });
          if (result.archived)
            await cacheIncidentArchive(scope, threadId, true);
          return result;
        },
        unarchiveThread: async (threadId) => {
          const result =
            await trpcClient.incidentsRouter.unarchiveThread.mutate({
              threadId,
            });
          if (!result.archived)
            await cacheIncidentArchive(scope, threadId, false);
          return result;
        },
        appendMessage: async (input) =>
          trpcClient.incidentsRouter.appendMessage.mutate(input),
        updateMessage: async (messageId, updates) =>
          trpcClient.incidentsRouter.updateMessage.mutate({
            messageId,
            ...updates,
          }),
      },
      runbooks: {
        list: async () => trpcClient.runbooksRouter.listRunbooks.query(),
        get: async (id) => trpcClient.runbooksRouter.getRunbook.query({ id }),
        create: async (input) =>
          saveRunbookMutation(scope, () =>
            trpcClient.runbooksRouter.createRunbook.mutate({
              title: input.title,
              description: input.description,
              idleTimeout: input.idleTimeout,
              actions: input.actions?.map(sanitizeRunbookActionForDashboard),
            }),
          ),
        updateMetadata: async (id, metadata) =>
          saveRunbookMutation(scope, () =>
            trpcClient.runbooksRouter.updateRunbookMetadata.mutate({
              id,
              ...metadata,
            }),
          ),
        updateActions: async (id, actions) =>
          saveRunbookMutation(scope, () =>
            trpcClient.runbooksRouter.updateRunbookActions.mutate({
              id,
              actions: actions.map(sanitizeRunbookActionForUpdate),
            }),
          ),
        saveAction: async (id, action, sortOrder) =>
          saveRunbookMutation(scope, () =>
            trpcClient.runbooksRouter.saveRunbookAction.mutate({
              runbookId: id,
              action: sanitizeRunbookActionForSave(action, sortOrder),
            }),
          ),
        deleteAction: async (id, actionId) =>
          saveRunbookMutation(scope, () =>
            trpcClient.runbooksRouter.deleteRunbookAction.mutate({
              runbookId: id,
              actionId,
            }),
          ),
        reorderActions: async (id, actionIdsInOrder) =>
          saveRunbookMutation(scope, () =>
            trpcClient.runbooksRouter.reorderRunbookActions.mutate({
              runbookId: id,
              actionIdsInOrder,
            }),
          ),
        delete: async (id) => {
          const result = await trpcClient.runbooksRouter.deleteRunbook.mutate({
            id,
          });
          await cacheDeletedRunbook(scope, id);
          return result;
        },
        exportContext: async (id) => {
          const result =
            await trpcClient.runbooksRouter.exportRunbookContext.query({ id });
          if (result === null) {
            throw new Error("Runbook context not found");
          }
          return result as RunbookContextV1;
        },
        exportRunbooks: async (input) =>
          (await trpcClient.runbooksRouter.exportRunbooks.mutate(
            input,
          )) as RunbookExportArtifactV1,
        importRunbooks: async (input) => {
          const result = await trpcClient.runbooksRouter.importRunbooks.mutate({
            artifact: input.artifact,
            options: input.options,
          });
          if (!input.options?.dryRun) {
            void queryClient.invalidateQueries({
              queryKey: dashboardKeys.runbooks(scope),
            });
          }
          return result;
        },
        listTelemetryNeeds: async () =>
          trpcClient.runbooksRouter.listTelemetryNeeds.query(),
        execute: async (input) => {
          const result = await cloudRunbookGateway.start({
            runbookId: input.runbookId,
            requestKey: input.requestKey ?? crypto.randomUUID(),
            expectedRevisionNumber: input.expectedRevisionNumber,
            incidentId: input.incidentThreadId,
            parameterValues: input.parameterValues,
            source: "manual",
            triggerContext: normalizeTriggerContext(input.triggerContext),
          });
          return {
            executionId: result.executionId,
            resultId: result.resultId,
          };
        },
        continueDiagnosis: async (input) =>
          trpcClient.runbooksRouter.continueDiagnosis.mutate({
            diagnosisId: input.diagnosisId,
            telemetryEntryId: input.telemetryEntryId,
            sourceId: input.sourceId,
            incidentThreadId: input.incidentThreadId,
            llmProviderKey: toDashboardRunbookProviderKey(input.llmProviderKey),
            llmModel: input.llmModel,
          }),
        getExecution: async (executionId) =>
          normalizeRunbookExecution(
            await trpcClient.runbooksRouter.getExecution.query({ executionId }),
          ),
        listExecutions: async (filters) => {
          const result = await trpcClient.runbooksRouter.listExecutions.query({
            status: filters?.status,
            limit: filters?.limit ?? 50,
            offset: filters?.offset ?? 0,
            runbookId: filters?.runbookId,
          });

          return {
            executions: result.executions
              .map(normalizeRunbookExecutionSummary)
              .filter(
                (execution): execution is RunbookExecutionSummaryRecord =>
                  execution !== null,
              ),
            total: result.total,
            hasMore: result.hasMore,
          };
        },
        listTelemetryActivity: async (filters) => {
          const result =
            await trpcClient.runbooksRouter.listTelemetryActivity.query({
              status: filters?.status,
              limit: filters?.limit ?? 25,
              offset: filters?.offset ?? 0,
            });

          return {
            executions: result.executions
              .map(normalizeRunbookExecutionSummary)
              .filter(
                (execution): execution is RunbookExecutionSummaryRecord =>
                  execution !== null,
              ),
            total: result.total,
            hasMore: result.hasMore,
          };
        },
        getLinkedTelemetryExecution: async (input) =>
          normalizeRunbookExecutionSummary(
            await trpcClient.runbooksRouter.getLinkedTelemetryExecution.query(
              input,
            ),
          ),
        cancelExecution: async (executionId) =>
          trpcClient.runbooksRouter.cancelExecution.mutate({ executionId }),
        onExecutionEvent: (handler) => {
          const unsubscribeExecution = webSocketService.on(
            "runbook:execution",
            (data) => {
              handler(data as Parameters<typeof handler>[0]);
            },
          );
          const pollInterval = 5000; // Poll every 5 seconds
          let pollTimer: ReturnType<typeof setInterval> | null = null;
          let pollInFlight = false;
          let stopped = false;

          const stopPolling = () => {
            if (pollTimer !== null) {
              clearInterval(pollTimer);
              pollTimer = null;
            }
          };

          const pollActiveExecutions = async () => {
            if (pollInFlight || stopped) {
              return;
            }

            pollInFlight = true;
            try {
              const result =
                await trpcClient.runbooksRouter.listExecutions.query({
                  limit: 100,
                  offset: 0,
                });
              // eslint-disable-next-line sonarjs/no-nested-functions -- The filter closes over the local polling lifecycle and is kept beside the stop guard.
              const activeExecutions = result.executions.filter((execution) =>
                isActiveExecutionStatus(execution.status),
              );

              await Promise.all(
                // eslint-disable-next-line sonarjs/no-nested-functions -- Each fetch must observe this polling cycle's stopped flag before emitting an update.
                activeExecutions.map(async (executionSummary) => {
                  const execution = normalizeRunbookExecution(
                    await trpcClient.runbooksRouter.getExecution.query({
                      executionId: executionSummary.executionId,
                    }),
                  );

                  if (execution === null || stopped) {
                    return;
                  }

                  handler({
                    resultId: execution.executionId,
                    executionId: execution.executionId,
                    incidentThreadId:
                      execution.incidentThreadId ??
                      executionSummary.incidentThreadId ??
                      null,
                    execution,
                  });
                }),
              );
            } catch (error) {
              console.error("Error polling execution:", error);
            } finally {
              pollInFlight = false;
            }
          };

          const startPolling = () => {
            if (stopped || pollTimer !== null) {
              return;
            }

            void pollActiveExecutions();
            // eslint-disable-next-line sonarjs/no-nested-functions -- The interval callback shares this subscription's polling and teardown state.
            pollTimer = setInterval(() => {
              void pollActiveExecutions();
            }, pollInterval);
          };

          const unsubscribeConnect = webSocketService.on("connect", () => {
            stopPolling();
          });
          const unsubscribeDisconnect = webSocketService.on(
            "disconnect",
            () => {
              startPolling();
            },
          );

          if (!webSocketService.isConnected()) {
            startPolling();
          }

          return () => {
            stopped = true;
            stopPolling();
            unsubscribeExecution();
            unsubscribeConnect();
            unsubscribeDisconnect();
          };
        },
        onChanged: () => () => {},
      },
    }),
    [cloudRunbookGateway, scope],
  );

  const ticketResolutionService = useMemo<TicketResolutionService>(
    () => ({
      getDiagnosisTickets: async (diagnosisIds) =>
        z.array(diagnosisTicketSchema).parse(
          await trpcClient.diagnosisRouter.getDiagnosisTickets.query({
            diagnosisIds,
          }),
        ) as DiagnosisTicket[],
      createDiagnosisTicket: (input) =>
        trpcClient.diagnosisRouter.createTicketFromDiagnosis.mutate(input),
      getResolvedTicket: async (id) =>
        resolvedTicketDetailsSchema
          .nullable()
          .parse(
            await trpcClient.ticketsRouter.getResolvedTicketById.query({ id }),
          ) as ResolvedTicketDetails | null,
      getResolvedTickets: async (filters = {}) =>
        paginatedResolvedTicketsSchema.parse(
          await trpcClient.ticketsRouter.getResolvedTickets.query({
            resolutionType: filters.resolutionType as
              | CoreResolutionType[]
              | undefined,
            priority: filters.priority as CoreTicketPriority[] | undefined,
            provider: filters.provider as CoreTicketProviderType | undefined,
            hasLessonsLearned: filters.hasLessonsLearned,
            page: filters.page ?? 1,
            limit: filters.limit ?? 20,
            sortBy: filters.sortBy ?? "ticketResolvedAt",
            sortOrder: filters.sortOrder ?? "desc",
          }),
        ) as PaginatedResolvedTickets,
      getResolvedSummary: async () =>
        resolvedTicketsSummarySchema.parse(
          await trpcClient.ticketsRouter.getResolvedSummary.query(),
        ) as ResolvedTicketsSummary,
      updateResolutionMetadata: async ({ id, data }) =>
        resolvedTicketDetailsSchema.parse(
          await trpcClient.ticketsRouter.updateResolutionMetadata.mutate({
            id,
            data: data as unknown as Parameters<
              typeof trpcClient.ticketsRouter.updateResolutionMetadata.mutate
            >[0]["data"],
          }),
        ) as ResolvedTicketDetails,
      sendResolutionNotesToItop: async (id) =>
        resolvedTicketDetailsSchema.parse(
          await trpcClient.ticketsRouter.sendResolutionNotesToItop.mutate({
            id,
          }),
        ) as ResolvedTicketDetails,
      syncResolutionStatuses: async (input = {}) =>
        syncResultSchema.parse(
          await trpcClient.ticketsRouter.syncResolutionStatuses.mutate({
            provider: input.provider as Parameters<
              typeof trpcClient.ticketsRouter.syncResolutionStatuses.mutate
            >[0]["provider"],
            batchSize: input.batchSize,
          }),
        ) as SyncResult,
      syncTicketStatus: async (id) =>
        resolvedTicketDetailsSchema.parse(
          await trpcClient.ticketsRouter.syncTicketStatus.mutate({ id }),
        ) as ResolvedTicketDetails,
    }),
    [],
  );

  const runtimeServices = useMemo<FrontendBitsentryServicePorts["runtime"]>(
    () => ({
      getAuthSession: () => ({
        user: user ?? null,
        isAuthenticated,
        isLoading,
      }),
      logout,
      navigate: (path: string) => {
        window.location.assign(path);
      },
      getConnectionStatus: () => isConnected,
    }),
    [isAuthenticated, isConnected, isLoading, logout, user],
  );

  const services = useMemo<FrontendBitsentryServicePorts>(
    () => ({
      ...staticServices,
      runtime: runtimeServices,
    }),
    [runtimeServices, staticServices],
  );

  return (
    <TicketResolutionProvider service={ticketResolutionService}>
      <BitsentryServicesProvider services={services}>
        {children}
      </BitsentryServicesProvider>
    </TicketResolutionProvider>
  );
}
